import { Helmet } from "react-helmet-async";
import { CustomTabs, DaysNavigator, SideActivityCard, WeatherDisplay } from "@/components";
import { PlanCarousel } from "@/components/plan/PlanCarousel";
import { PlanQuickInfo } from "@/components/plan/PlanQuickInfo";
import Footer from "@/components/layout/Footer";
import Header from "@/components/layout/Header"
import MustDoCard from "@/components/MustDoCard";
import { AboutUs } from "@/features/landing"
import { AppPromo } from "@/features/landing"
import { PlansSidebar, usePlanDetails } from "@/features/planner";
import { Typography, CircularProgress, Alert } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react";
import TodoIcon from "@/assets/mdi_to-do.svg";
import SideIcon from "@/assets/side.svg";
import { DestinationImagesCarousel } from "@/components/plan/DestinationImagesCarousel";
import { DestinationWarning } from "@/components/plan/DestinationWarning";
import { AxiosError } from "axios";

export const Route = createFileRoute("/plans/$planId")({
    component: RouteComponent,
})

function RouteComponent() {
    const { planId } = Route.useParams();
    const [showPlans, setShowPlans] = useState(false);
    const [selectedDay, setSelectedDay] = useState(0);
    const [timeSlotsByDay, setTimeSlotsByDay] = useState<Record<number, 'morning' | 'afternoon' | 'evening' | 'weather'>>({});

    // Derived state for current day's time slot
    const selectedTimeSlot = timeSlotsByDay[selectedDay] || 'morning';

    const { data: planData, isLoading, error } = usePlanDetails(planId);

    useEffect(() => {
        setSelectedDay(0);
        setTimeSlotsByDay({});
    }, [planId])

    if (isLoading) {
        return (
            <main className="bg-[#fff3ea] text-slate-900">
                <Header onOpenPlans={() => setShowPlans(true)} />
                <div className="flex items-center justify-center min-h-screen">
                    <CircularProgress sx={{ color: '#187A85' }} />
                </div>
                <PlansSidebar open={showPlans} onClose={() => setShowPlans(false)} />
            </main>
        );
    }

    if (error) {
        const axiosError = error as AxiosError;
        const errorStatus = axiosError.status;
        return (
            <main className="bg-[#fff3ea] text-slate-900">
                <Header onOpenPlans={() => setShowPlans(true)} />
                <div className="w-full min-h-[40vh] flex flex-col items-center justify-center bg-white border-b border-[#EAEAEA]">
                    <div className="max-w-4xl w-full px-6 text-center">
                        <Typography variant="h3" sx={{
                            fontFamily: 'serif',
                            fontWeight: 'bold',
                            color: '#E76828',
                            marginBottom: 2
                        }}>
                            {errorStatus === 404 ? "Destination Not Found" : "Wanderlust Interrupted"}
                        </Typography>
                        <Alert
                            severity="error"
                            sx={{
                                width: '100%',
                                fontSize: '1.1rem',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 3,
                                backgroundColor: '#FEF2F2',
                                color: '#991B1B',
                                '& .MuiAlert-icon': {
                                    fontSize: '2rem'
                                }
                            }}
                        >
                            {errorStatus === 404 ? "The plan you are looking for doesn't exist or has been moved." : "We're having trouble retrieving this plan. Please check your connection or try again later."}
                        </Alert>
                    </div>
                </div>
                <PlansSidebar open={showPlans} onClose={() => setShowPlans(false)} />
            </main>
        );
    }

    if (!planData) {
        return null;
    }

    const { plan } = planData;
    const currentDay = plan.itinerary.days[selectedDay];
    const timeSlotData = selectedTimeSlot !== 'weather' ? currentDay?.[selectedTimeSlot] : null;

    return <main className="bg-[#fff3ea] text-slate-900">
        <Helmet>
            <title>{`${plan?.itinerary?.destination} ${plan?.itinerary?.days?.length}-Day Itinerary | TravelHug`}</title>
        </Helmet>
        <Header onOpenPlans={() => setShowPlans(true)} />

        <div className="px-4 pt-4">
            {
                planData.destination_warnings?.length > 0 ? (
                    <DestinationWarning warning={planData.destination_warnings} />
                ) : (
                    <div className="px-4 md:px-8 pt-4 md:pt-8 flex justify-center w-full overflow-hidden">
                        <DestinationImagesCarousel images={plan.destination_images} destination={plan.itinerary.destination} />
                    </div>
                )
            }
        </div>




        <div className="px-4 md:px-8 pt-4">
            <Typography
                variant="h5"
                sx={{
                    color: "#187A85",
                    fontWeight: 600,
                    fontSize: { xs: "20px", md: "28px" },
                    lineHeight: 1.3,
                }}
            >
                {plan.itinerary.title}
            </Typography>
            <Typography
                sx={{
                    marginBottom: 2,
                    color: '#E76828',
                    fontSize: '16px',
                    lineHeight: 1
                }}
                variant="h4"
            >
                {plan.itinerary.days[selectedDay].day_display}
            </Typography>
            <DaysNavigator
                days={plan.itinerary.days}
                selectedDay={selectedDay}
                onDaySelect={setSelectedDay}
            />
            <div className="p-4 md:p-8 bg-white mt-2" style={{ borderRadius: 16 }}>
                <CustomTabs
                    tabs={[
                        { label: "Morning", value: "morning" },
                        { label: "Afternoon", value: "afternoon" },
                        { label: "Evening", value: "evening" },
                        { label: "Weather", value: "weather" },
                    ]}
                    value={selectedTimeSlot}
                    onChange={(value) => {
                        setTimeSlotsByDay(prev => ({
                            ...prev,
                            [selectedDay]: value as 'morning' | 'afternoon' | 'evening' | 'weather'
                        }));
                    }}
                />

                {selectedTimeSlot !== 'weather' && timeSlotData && (
                    <>
                        <div className="mt-8 mb-6">
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "16px",
                                    color: "#1e293b"
                                }}
                            >
                                Discovering {plan.itinerary.destination}
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: "#64748b",
                                    fontSize: "14px",
                                    lineHeight: 1.5,
                                }}
                            >
                                {timeSlotData.description}
                            </Typography>
                        </div>
                        <div className="py-3 px-6 mt-6 rounded-2xl flex items-center gap-3 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-slate-100">
                            <img src={TodoIcon} alt="todo icon" className="w-[32px] h-[32px]" />
                            <Typography
                                variant="h5"
                                sx={{
                                    color: "#187A85",
                                    fontWeight: 700,
                                    fontSize: "24px",
                                    letterSpacing: "-0.02em",
                                }}
                            >
                                Must Do
                            </Typography>
                        </div>
                        {timeSlotData.must_do && timeSlotData.must_do.length > 0 && (
                            <div className="flex flex-col md:flex-row gap-4 py-4 overflow-x-auto">
                                {timeSlotData.must_do.map((activity, index) => (
                                    <MustDoCard key={index} activity={activity} />
                                ))}
                            </div>
                        )}
                        <div className="py-3 px-6 mt-6 rounded-2xl flex items-center gap-3 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)] border border-slate-100">
                            <img src={SideIcon} alt="todo icon" className="w-[32px] h-[32px]" />
                            <Typography
                                variant="h5"
                                sx={{
                                    color: "#187A85",
                                    fontWeight: 600,
                                    fontSize: "28px",
                                    lineHeight: 1.3,
                                }}
                            >
                                Side Activities
                            </Typography>
                        </div>
                        {timeSlotData.side_activities && timeSlotData.side_activities.length > 0 && (
                            <div className="flex flex-col md:flex-row gap-4 py-4 overflow-x-auto">
                                {timeSlotData.side_activities.map((activity, index) => (
                                    <SideActivityCard key={index} activity={activity} />
                                ))}
                            </div>
                        )}

                        {timeSlotData &&
                            <div className="mt-4">
                                <PlanQuickInfo
                                    day={currentDay}
                                    timeSlot={timeSlotData}
                                    timeSlotName={selectedTimeSlot}
                                />
                            </div>
                        }
                    </>
                )}

                {selectedTimeSlot === 'weather' && currentDay?.weather && (
                    <WeatherDisplay
                        weather={currentDay.weather}
                        title={`Discovering ${plan.itinerary.destination}`}
                        description={currentDay.weather.condition}
                    />
                )}
            </div>
            <PlanCarousel plan={plan} />
        </div>
        <AppPromo />
        <Footer />

        <PlansSidebar open={showPlans} onClose={() => setShowPlans(false)} />
    </main>
}

